import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

/**
 * StepProgress — Compact text-only step indicator for multi-step patient forms.
 *
 * Renders a single-line label with a thin progress bar beneath:
 *   "Step 2 of 5 · Treatment Risks"
 *   ████████████░░░░░░░░░░░░░░░░░░░░
 *
 * Total height: ~40px. No clickable dots — navigation is via Back/Continue only.
 * Designed to sit directly below the brand header.
 */
export default function StepProgress({ steps, currentStep, primaryColor: primaryColorProp }) {
    const total = steps.length;
    const primaryColor = primaryColorProp || '#212B36';
    const stepName = steps[currentStep]?.name || '';

    return (
        <Box sx={{ mb: 3 }}>
            {/* Step label — "Step 2 of 5 · Treatment Risks" */}
            <Typography sx={{
                textAlign: 'center',
                fontSize: { xs: '13px', sm: '14px' },
                color: '#637381',
                mb: 1.5,
                lineHeight: 1.5,
                fontWeight: 400,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
            }}>
                Step {currentStep + 1} of {total}
                {stepName && (
                    <>
                        {' \u2014 '}
                        <Box component="span" sx={{ color: '#1A2027', fontWeight: 500 }}>
                            {stepName}
                        </Box>
                    </>
                )}
            </Typography>

            {/* Segmented progress bar — one segment per step */}
            <Box sx={{ display: 'flex', gap: '3px' }}>
                {steps.map((step, idx) => (
                    <Box
                        key={step.id || idx}
                        sx={{
                            flex: 1,
                            height: 3,
                            borderRadius: 2,
                            bgcolor: idx <= currentStep ? primaryColor : '#E5E7EB',
                            transition: 'background-color 0.35s ease-out',
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

StepProgress.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string.isRequired,
    })).isRequired,
    currentStep: PropTypes.number.isRequired,
    primaryColor: PropTypes.string,
};

StepProgress.defaultProps = {
    primaryColor: '#00AB55',
};
